import React, { useState, useMemo } from 'react';
import useGameStore from '../../store/gameStore';
import { MANUFACTURER_FINANCE, getManufacturerLoyaltyTier, DURATION_OPTIONS } from '../../data/manufacturerFinance';
import { formatMoney } from '../../utils/format';
import { Factory, X, Check, ChevronRight, Star, AlertCircle, Sparkles } from 'lucide-react';

/**
 * Manufacturer Finance Modal — third option alongside cash purchase & leasing.
 *
 * Opens when the player clicks "Financement constructeur" on a vehicle row.
 * Lets them pick a 36/48/60 month duration and shows the resulting quote
 * (rate, down-payment, monthly, total cost, total interest, loyalty tier).
 *
 * Closing actions:
 *   - Cancel → simply closes
 *   - Signer → calls store.financeVehicleWithManufacturer + closes
 *
 * No back-end. Pure Zustand.
 */
export const ManufacturerFinanceModal = ({ vehicleData, onClose }) => {
  const computeQuote = useGameStore(s => s.computeManufacturerFinanceQuote);
  const financeWithMfg = useGameStore(s => s.financeVehicleWithManufacturer);
  const treasury = useGameStore(s => s.treasury);
  const relations = useGameStore(s => s.manufacturerRelations);

  const [duration, setDuration] = useState(48);
  const [error, setError] = useState(null);

  const mfg = MANUFACTURER_FINANCE[vehicleData.brand];
  const relation = relations?.[vehicleData.brand] || null;
  // Narrow the memo deps to the only fields that actually influence the quote:
  // `units` (loyalty discount + residual rebate) and `defaulted` (eligibility).
  // Without this narrowing, any unrelated relation mutation (e.g. other brand)
  // would re-trigger the quote computation.
  const relationDeps = `${relation?.units || 0}:${relation?.defaulted ? 1 : 0}`;
  const quote = useMemo(
    () => (computeQuote ? computeQuote(vehicleData.id, duration) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vehicleData.id, duration, computeQuote, relationDeps]
  );
  // 18/06/2026 — le taux varie selon la durée : on calcule les 3 devis pour
  // afficher le taux propre à chaque durée sur les boutons.
  const quotesByDuration = useMemo(
    () => Object.fromEntries(DURATION_OPTIONS.map(d => [d, computeQuote ? computeQuote(vehicleData.id, d) : null])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vehicleData.id, computeQuote, relationDeps]
  );

  if (!mfg) {
    return null; // brand has no captive finance arm
  }

  const handleSign = () => {
    setError(null);
    const result = financeWithMfg(vehicleData.id, duration);
    if (result && result.ok) {
      onClose();
    } else {
      setError(result?.detail || result?.reason || 'Échec de la signature du financement.');
    }
  };

  const tier = getManufacturerLoyaltyTier(vehicleData.brand, relation?.units || 0);
  const canAffordDownPayment = quote && quote.eligible && treasury >= quote.downPayment;

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="manufacturer-finance-overlay"
    >
      <div
        className="bg-ink-800 rounded-xl w-full max-w-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-200 max-h-[92vh] flex flex-col"
        data-testid="manufacturer-finance-modal"
      >
        <div
          className="px-5 py-3 flex items-center justify-between flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${mfg.color} 0%, ${mfg.color}DD 100%)` }}
        >
          <div className="flex items-center gap-2 text-white">
            <Factory className="w-5 h-5" />
            <div>
              <h3 className="font-bold text-base">{mfg.name}</h3>
              <p className="text-[11px] italic opacity-90">{mfg.tagline}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="close-mfg-finance">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {/* Vehicle summary */}
          <div className="flex items-center gap-3 p-3 rounded-lg border border-ink-600 bg-ink-700/30">
            {vehicleData.image && (
              <img src={vehicleData.image} alt={`${vehicleData.brand} ${vehicleData.model}`} className="w-24 h-16 rounded-md object-cover border border-ink-600" />
            )}
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-100">{vehicleData.brand} {vehicleData.model}</p>
              <p className="text-[11px] text-slate-500">Prix catalogue {formatMoney(vehicleData.price)} · Tier {vehicleData.tier}</p>
            </div>
          </div>

          {/* Loyalty tier banner */}
          <div
            className="rounded-lg border p-3 flex items-center gap-3"
            style={{ borderColor: `${mfg.color}40`, background: `${mfg.color}08` }}
            data-testid="mfg-loyalty-banner"
          >
            <Star className="w-5 h-5 flex-shrink-0" style={{ color: mfg.color }} />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold" style={{ color: mfg.color }}>
                Votre statut : {tier.label}
              </p>
              <p className="text-[11px] text-slate-400">
                {relation?.units || 0} véhicule·s financé·s · Remise sur taux : {(tier.discount * 100).toFixed(2)} pp
                {tier.residualRebate > 0 && ` · Ristourne résiduelle : ${Math.round(tier.residualRebate * 100)} %`}
              </p>
              {tier.nextTier && (
                <p className="text-[10px] text-slate-500 mt-0.5">
                  Encore {tier.nextTier.units - (relation?.units || 0)} financement·s pour atteindre « {tier.nextTier.label} »
                </p>
              )}
            </div>
          </div>

          {/* Duration selector */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Durée du financement</p>
            <div className="grid grid-cols-3 gap-2" data-testid="mfg-duration-selector">
              {DURATION_OPTIONS.map(d => {
                const q = quotesByDuration[d];
                return (
                  <button
                    key={d}
                    onClick={() => setDuration(d)}
                    className={`py-2 rounded-md border-2 text-xs font-bold transition-all flex flex-col items-center gap-0.5 ${
                      duration === d
                        ? 'border-money bg-money/15 text-money'
                        : 'border-ink-600 text-slate-400 hover:border-ink-500'
                    }`}
                    data-testid={`mfg-duration-${d}`}
                  >
                    <span>{d} mois</span>
                    {q?.eligible && (
                      <span className={`text-[10px] font-mono ${duration === d ? 'text-money/80' : 'text-slate-500'}`}>
                        {q.ratePercent} %
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-500 mt-1.5">
              Le taux augmente avec la durée : mensualités plus légères, mais coût total plus élevé.
            </p>
          </div>

          {/* 18/06/2026 — Gestes commerciaux du palier fidélité */}
          {quote?.eligible && (quote.downPaymentPct < 0.10 || quote.firstMonthFree || quote.extraRateDiscount > 0) && (
            <div
              className="rounded-md border border-money/30 bg-money/5 px-3 py-2"
              data-testid="mfg-gestures"
            >
              <p className="text-[10px] uppercase tracking-wider text-money font-bold mb-1">
                🤝 Gestes commerciaux — {quote.tier}
              </p>
              <ul className="space-y-0.5 text-[11px] text-slate-300">
                {quote.downPaymentPct < 0.10 && (
                  <li data-testid="mfg-gesture-down">• Apport réduit à <strong className="text-money">{Math.round(quote.downPaymentPct * 100)} %</strong> (au lieu de 10 %)</li>
                )}
                {quote.firstMonthFree && (
                  <li data-testid="mfg-gesture-firstmonth">• <strong className="text-money">1ère mensualité offerte</strong> par le constructeur ({formatMoney(quote.monthly)})</li>
                )}
                {quote.extraRateDiscount > 0 && (
                  <li data-testid="mfg-gesture-rate">• Baisse du taux de <strong className="text-money">−{(quote.extraRateDiscount * 100).toFixed(1)} pt</strong> (partenaire stratégique)</li>
                )}
              </ul>
            </div>
          )}

          {/* Quote summary */}
          {quote && quote.eligible && (
            <div className="rounded-md border border-ink-600 bg-ink-700/30 divide-y divide-ink-600 text-xs" data-testid="mfg-quote-summary">
              <Row label="Taux annuel"          value={`${quote.ratePercent} %`} testId="mfg-rate" />
              <Row label={`Apport (${Math.round(quote.downPaymentPct * 100)} %)`} value={formatMoney(quote.downPayment)} testId="mfg-down-payment" />
              <Row label="Capital financé"     value={formatMoney(quote.financed)} testId="mfg-financed" />
              <Row label="Mensualité"          value={`${formatMoney(quote.monthly)} / mois`} testId="mfg-monthly" />
              {quote.firstMonthFree && (
                <Row label="1ère mensualité"   value="Offerte 🎁" color="strong" testId="mfg-first-month" />
              )}
              <Row label="Intérêts cumulés"    value={formatMoney(quote.totalInterest)} color="warn" testId="mfg-interest" />
              <Row label="Coût total"          value={formatMoney(quote.totalCost)} color="strong" testId="mfg-total" />
            </div>
          )}

          {!canAffordDownPayment && quote?.eligible && (
            <div className="rounded-md border border-danger/40 bg-danger/5 px-3 py-2 flex items-center gap-2" data-testid="mfg-insufficient">
              <AlertCircle className="w-4 h-4 text-danger flex-shrink-0" />
              <p className="text-[12px] text-danger">
                Apport insuffisant : il vous manque {formatMoney(quote.downPayment - treasury)} pour signer.
              </p>
            </div>
          )}

          {quote && !quote.eligible && (
            <div className="rounded-md border border-danger/40 bg-danger/5 px-3 py-2 flex items-center gap-2" data-testid="mfg-refused">
              <AlertCircle className="w-4 h-4 text-danger flex-shrink-0" />
              <p className="text-[12px] text-danger">{quote.reason}</p>
            </div>
          )}

          {error && (
            <div className="rounded-md border border-danger/40 bg-danger/5 px-3 py-2 flex items-center gap-2" data-testid="mfg-error">
              <AlertCircle className="w-4 h-4 text-danger flex-shrink-0" />
              <p className="text-[12px] text-danger">{error}</p>
            </div>
          )}

          {/* Pedagogical footer */}
          <div className="rounded-md bg-ink-700/30 border border-ink-600 px-3 py-2 flex items-start gap-2">
            <Sparkles className="w-3.5 h-3.5 text-accent flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-slate-400 leading-relaxed">
              <strong className="text-slate-300">Pourquoi financer chez le constructeur ?</strong>{' '}
              Procédure simplifiée (pas de scoring bancaire), relation directe avec la marque,
              et avantages cumulables au fil des achats : remise sur taux, apport réduit,
              1ère mensualité offerte, ristourne sur valeur résiduelle.
              En contrepartie, le taux de base est plus élevé qu'un crédit bancaire — et il monte avec la durée choisie.
            </p>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-ink-600 bg-ink-700/30 flex items-center justify-between flex-shrink-0">
          <button
            onClick={onClose}
            className="text-xs text-slate-400 hover:text-slate-200 px-3 py-2 transition-colors"
            data-testid="mfg-cancel"
          >
            Annuler
          </button>
          <button
            onClick={handleSign}
            disabled={!quote || !quote.eligible || !canAffordDownPayment}
            className="bg-money text-ink-900 px-4 py-2 rounded-lg text-sm font-bold hover:bg-money/85 transition-colors flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="mfg-sign"
          >
            <Check className="w-4 h-4" />
            Signer chez {mfg.name}
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, color, testId }) => (
  <div className="px-3 py-2 flex items-center justify-between" data-testid={testId}>
    <span className="text-slate-400">{label}</span>
    <span className={`font-bold ${color === 'warn' ? 'text-warn' : color === 'strong' ? 'text-money text-sm' : 'text-slate-200'}`}>
      {value}
    </span>
  </div>
);

export default ManufacturerFinanceModal;
